import { CartItem } from '../contexts/CartContext';
import type { PackageImage, TransferDirectionValue } from '../types';
import { ProductType } from '../types/enums';
import { formatCalendarDate } from './formatters';
import { propertyPath, servicePath } from './listingPaths';
import { placeholderImageUrl } from './placeholderImage';
import { UNDATED_DATE_LABEL, formatOccurrenceLine, isUndatedService } from './schedulingMode';

/**
 * The primary-flagged image URL in a package image list, falling back to the
 * first image in display order — `undefined` for an empty/absent list.
 * Mirrors the primary-first convention DiscoverPage's `primaryImageIn` and
 * `packageImages`' `orderImagesPrimaryFirst` use.
 */
const primaryPackageImageUrl = (images: PackageImage[] | undefined): string | undefined =>
  images?.find((image) => image.is_primary)?.url || images?.[0]?.url;

/**
 * Composes the human-readable route for a chauffeur-transfer cart/checkout
 * line, e.g. "Vail → Denver International Airport (DEN)" for an authored
 * hub, or a directional phrase (e.g. "From Denver International Airport
 * (DEN)") when the parent's hub has been cleared. Single source of truth for
 * this string — reused by `deriveCartItemDisplay` (cart line) and
 * `checkoutSummary` (checkout summary line) so the composition never
 * duplicates across the two surfaces. Mirrors the same hub/direction table
 * as `backend/models/enums.py::compose_transfer_direction_label`.
 */
export const composeTransferRouteLabel = (
  destinationName: string,
  direction: TransferDirectionValue,
  hubLabel: string | null,
): string => {
  if (!hubLabel) {
    return direction === 'from_hub' ? `From ${destinationName}` : `To ${destinationName}`;
  }
  return direction === 'from_hub'
    ? `${hubLabel} → ${destinationName}`
    : `${destinationName} → ${hubLabel}`;
};

/**
 * The display shape returned by deriveCartItemDisplay.
 *
 * - `name`: the parent/service name (or accommodation name). Always the
 *   top-level entity name — for services with a selected child variant this
 *   is still the parent name; the child name is exposed separately via
 *   `variantName`.
 * - `variantName`: the selected child variant name, present only when the
 *   cart item carries a variant (i.e. `item.variant` is set). Callers
 *   should render this as a secondary/eyebrow line beneath `name`.
 * - `typeLabel`: badge copy ("Stay" / "Service" / "").
 * - `location`: the property or service location string.
 * - `meta`: date / guest-count summary line — a date range for a stay, a
 *   single date (and time) for a service, or a package's start date plus
 *   its nights count (a package carries no end date).
 * - `image`: image URL (falls back to an inline SVG placeholder).
 * - `listingPath`: the route to the item's own listing page
 *   (`/property/:slug`, `/service/:slug`, `/packages/:slug`), falling back to
 *   the id when a persisted line has no slug, and using the parent even
 *   when a variant is carted — the variant picker lives on the parent's page. `null` for the fallback shape, which has no listing.
 */
export interface CartItemDisplayShape {
  name: string;
  variantName: string | undefined;
  typeLabel: string;
  location: string;
  meta: string;
  image: string;
  listingPath: string | null;
}

/**
 * Derives all display fields for a single cart item.
 *
 * This is the single source of truth for cart-line display derivation,
 * shared by CartPage (full cart view) and CartDropdown (header popover)
 * so both surfaces render identically. Guards every optional field with
 * `!= null` because the API returns null (not undefined) for absent values.
 */
export const deriveCartItemDisplay = (
  item: Omit<CartItem, 'id' | 'dateAdded' | 'isByRequest'>,
): CartItemDisplayShape => {
  if (item.type === ProductType.ACCOMMODATION && item.accommodation) {
    const checkIn =
      item.checkInDate != null
        ? (formatCalendarDate(item.checkInDate, { month: 'short', day: 'numeric' }) ?? '')
        : '';
    const checkOut =
      item.checkOutDate != null
        ? (formatCalendarDate(item.checkOutDate, { month: 'short', day: 'numeric' }) ?? '')
        : '';
    const dateRange = checkIn && checkOut ? `${checkIn} — ${checkOut}` : '';
    const guestPart = `${item.guestCount} guest${item.guestCount > 1 ? 's' : ''}`;
    return {
      name: item.accommodation.name,
      variantName: item.accommodationVariant?.name,
      typeLabel: 'Stay',
      location: item.accommodation.location ?? '',
      meta: [dateRange, guestPart].filter(Boolean).join(' · '),
      image:
        item.accommodation.images?.[0]?.url ||
        item.accommodation.primary_image_url ||
        placeholderImageUrl(item.accommodation.name),
      listingPath: propertyPath(item.accommodation),
    };
  }

  if (item.type === ProductType.PACKAGE && item.package) {
    // A package CartItem carries only a check-in date — the server derives
    // the real end date from the package's own duration_nights (see
    // toNativeCheckoutLine, which sends no end_date). The cart line shows
    // that start date alone, with the duration spelled out as a nights
    // count rather than a derived end date, so it never implies a range the
    // server itself does not compute here. Month + day only, matching the
    // accommodation and service rows on the same list (the checkout summary
    // rail, which shows the year, formats its own line).
    const startDate =
      item.checkInDate != null
        ? formatCalendarDate(item.checkInDate, { month: 'short', day: 'numeric' })
        : null;
    const durationNights = item.packageVariant?.duration_nights ?? item.package.duration_nights;
    const nightsPart =
      durationNights != null ? `${durationNights} night${durationNights === 1 ? '' : 's'}` : '';
    const guestPart = `${item.guestCount} guest${item.guestCount > 1 ? 's' : ''}`;
    return {
      name: item.package.name,
      variantName: item.packageVariant?.name,
      typeLabel: 'Package',
      // A package is not tied to a single property/service address, so it
      // carries no location line — unlike accommodations and services.
      location: '',
      meta: [startDate ?? '', nightsPart, guestPart].filter(Boolean).join(' · '),
      // A variant parent is a pure container that often carries no image of
      // its own — its images live on the tiers (ADR-0068). Fall back to the
      // carted tier's own image (the exact tier the guest chose) before the
      // house placeholder, so a tiered package whose parent has no hero image
      // still shows the tier it was carted as.
      image:
        primaryPackageImageUrl(item.package.images) ||
        primaryPackageImageUrl(item.packageVariant?.images) ||
        placeholderImageUrl(item.package.name),
      listingPath: `/packages/${item.package.slug}`,
    };
  }

  if (item.type === ProductType.SERVICE && item.service) {
    const dateLabel =
      item.serviceDate != null
        ? (formatCalendarDate(item.serviceDate, { month: 'short', day: 'numeric' }) ?? '')
        : '';
    const datePart = isUndatedService(item.service)
      ? UNDATED_DATE_LABEL
      : item.occurrence
        ? formatOccurrenceLine(item.occurrence)
        : [dateLabel, item.serviceTime].filter(Boolean).join(', ');
    const guestPart = `${item.guestCount} guest${item.guestCount > 1 ? 's' : ''}`;
    const variantName = item.transferDirection
      ? composeTransferRouteLabel(
          item.variant?.name ?? item.service.name,
          item.transferDirection,
          item.service?.transfer_hub_label ?? null,
        )
      : item.variant?.name;
    return {
      name: item.service.name,
      variantName,
      typeLabel: 'Service',
      location: item.service.location ?? '',
      meta: [datePart, guestPart].filter(Boolean).join(' · '),
      image: item.service.images?.[0]?.url || placeholderImageUrl(item.service.name),
      listingPath: servicePath(item.service),
    };
  }

  return {
    name: 'Item',
    variantName: undefined,
    typeLabel: '',
    location: '',
    meta: '',
    image: '',
    listingPath: null,
  };
};
